import React, { useState, useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient, ScanCommand } from "@aws-sdk/lib-dynamodb";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import awsconfig from '../aws-exports';
import './SeatingChart.css';

// Configuration
const TABLE_CAPACITY = 10;
const NUMBER_OF_TABLES = 15;

function SeatingChart() {
  const [rsvpData, setRsvpData] = useState([]);
  const [attendingGuests, setAttendingGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggedGuest, setDraggedGuest] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoadingSeating, setIsLoadingSeating] = useState(false);
  const [guestSearchTerm, setGuestSearchTerm] = useState('');
  const [editingTableName, setEditingTableName] = useState(null);
  const [tableNames, setTableNames] = useState({});
  const [editedTables, setEditedTables] = useState(new Set());
  const [hasLoadedSeating, setHasLoadedSeating] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('default');
  const [versions, setVersions] = useState(['default']);
  const [tableCapacities, setTableCapacities] = useState({});
  const [editingCapacity, setEditingCapacity] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [nextTableId, setNextTableId] = useState(NUMBER_OF_TABLES + 1);

  // Initialize tables dynamically
  useEffect(() => {
    const initialTables = Array.from({ length: NUMBER_OF_TABLES }, (_, index) => ({
      id: index + 1,
      name: `Table ${index + 1}`,
      guests: [],
      maxCapacity: tableCapacities[index + 1] || TABLE_CAPACITY
    }));
    setTables(initialTables);
  }, [tableCapacities]);

  // Load versions from DynamoDB
  const loadVersions = useCallback(async () => {
    setIsLoadingVersions(true);
    try {
      console.log('Loading versions from DynamoDB...');
      
      // Use the dedicated versions API endpoint
      const response = await fetch('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/versions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Versions API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Versions API error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Versions loaded:', result);
      
      if (result.versions && Array.isArray(result.versions)) {
        setVersions(result.versions);
        // If current version is not in the loaded versions, switch to default
        if (!result.versions.includes(currentVersion)) {
          setCurrentVersion('default');
        }
      }
      
    } catch (err) {
      console.error('Error loading versions:', err);
      // Keep default versions if loading fails
    } finally {
      setIsLoadingVersions(false);
    }
  }, [currentVersion]);

  // Load versions on component mount
  useEffect(() => {
    loadVersions();
  }, [loadVersions]);

  // Save versions to DynamoDB
  const saveVersions = useCallback(async (newVersions, action = 'update', versionName = '') => {
    console.log('=== Starting saveVersions operation ===');
    console.log('Parameters:', { newVersions, action, versionName });
    
    try {
      console.log('Attempting to save versions to DynamoDB...');
      
      // Use the dedicated versions API endpoint
      const response = await fetch('https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/versions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          versions: newVersions,
          action: action,
          versionName: versionName
        })
      });
      
      console.log('Versions API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Versions API error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Versions saved successfully:', result);
      
      if (result.versions) {
        console.log('Updating local versions state:', result.versions);
        setVersions(result.versions);
      }
      
      console.log('=== saveVersions operation completed successfully ===');
      
    } catch (err) {
      console.error('=== Error in saveVersions operation ===');
      console.error('Error details:', err);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      alert('Failed to save versions. Please try again.');
    }
  }, []);

  // Load existing seating chart data
  const loadSeatingChart = useCallback(async () => {
    // Only load if we haven't loaded before and have attending guests
    if (hasLoadedSeating || attendingGuests.length === 0) {
      return;
    }
    
    setIsLoadingSeating(true);
    try {
      console.log('Attempting to load seating chart data...');
      
      const response = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${currentVersion}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('API response status:', response.status);
      console.log('API response headers:', response.headers);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Seating chart API response:', result);
      
      if (result.seatingData && Array.isArray(result.seatingData) && result.seatingData.length > 0) {
        console.log('Found seating data:', result.seatingData);
        console.log('Current attending guests:', attendingGuests);
        console.log('Current tableNames state before loading:', tableNames);
        
        // Update tables with loaded seating data
        setTables(prevTables => {
          const newTableNames = {}; // Start with empty object to prevent caching
          console.log('Starting with empty newTableNames object');
          
          // Note: existingTablesMap was created for potential future use but is currently unused
          // const existingTablesMap = new Map(prevTables.map(table => [table.id, table]));
          
          // Process loaded seating data and create/update tables
          const updatedTables = result.seatingData.map(tableData => {
            console.log(`Processing table ${tableData.tableNumber} with guests:`, tableData.guests);
            console.log(`Table ${tableData.tableNumber} tableName from API:`, tableData.tableName);
            
            // Find guest objects by email
            const guestObjects = tableData.guests.map(email => 
              attendingGuests.find(guest => guest.id === email)
            ).filter(Boolean); // Remove undefined guests
            
            console.log(`Found ${guestObjects.length} valid guests for table ${tableData.tableNumber}`);
            
            // Create or update table
            const table = {
              id: tableData.tableNumber,
              name: tableData.tableName || `Table ${tableData.tableNumber}`,
              guests: guestObjects,
              maxCapacity: tableData.capacity || TABLE_CAPACITY
            };
            
            // Restore table name if it exists and is different from default
            if (tableData.tableName && tableData.tableName !== `Table ${tableData.tableNumber}`) {
              newTableNames[tableData.tableNumber] = tableData.tableName;
              console.log(`Restored table name for table ${tableData.tableNumber}: ${tableData.tableName}`);
            } else {
              console.log(`Table ${tableData.tableNumber} using default name or no custom name found`);
            }
            
            // Restore table capacity if it exists
            if (tableData.capacity && tableData.capacity !== TABLE_CAPACITY) {
              setTableCapacities(prev => ({
                ...prev,
                [tableData.tableNumber]: tableData.capacity
              }));
              console.log(`Restored table capacity for table ${tableData.tableNumber}: ${tableData.capacity}`);
            }
            
            return table;
          });
          
          // Update nextTableId to be higher than the highest table ID
          const maxTableId = Math.max(...updatedTables.map(table => table.id), NUMBER_OF_TABLES);
          setNextTableId(maxTableId + 1);
          
          // Update table names state
          setTableNames(newTableNames);
          
          // Clear edited tables since we're loading fresh data
          setEditedTables(new Set());
          
          console.log('Updated tables:', updatedTables);
          console.log('Final newTableNames object:', newTableNames);
          console.log('Next table ID set to:', maxTableId + 1);
          return updatedTables;
        });
      } else {
        console.log('No seating data found in response, keeping default tables');
        // Keep the default tables if no seating data is found
        setNextTableId(NUMBER_OF_TABLES + 1);
      }
      
      // Mark as loaded
      setHasLoadedSeating(true);
      
    } catch (err) {
      console.error('Error loading seating chart:', err);
      // Don't show error for loading - it's optional
      setHasLoadedSeating(true); // Mark as loaded even on error to prevent retries
    } finally {
      setIsLoadingSeating(false);
    }
  }, [attendingGuests, hasLoadedSeating, currentVersion]);

  // Load seating chart data after attending guests are loaded
  useEffect(() => {
    if (attendingGuests.length > 0) {
      loadSeatingChart();
    }
  }, [attendingGuests, loadSeatingChart]);

  // Fetch RSVP data
  const fetchRsvpData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Get credentials from Amplify Auth
      const currentSession = await Amplify.Auth.currentSession();
      const idToken = currentSession.getIdToken().getJwtToken();
      
      // Configure credential provider
      const region = awsconfig.aws_project_region;
      const identityPoolId = awsconfig.aws_cognito_identity_pool_id;
      
      // Create credentials provider
      const loginKey = `cognito-idp.${region}.amazonaws.com/${awsconfig.aws_user_pools_id}`;
      const logins = {};
      logins[loginKey] = idToken;
      
      const credentialsProvider = fromCognitoIdentityPool({
        client: new CognitoIdentityClient({ region }),
        identityPoolId: identityPoolId,
        logins: logins
      });

      // Create DynamoDB client
      const dynamoClient = new DynamoDBClient({
        region,
        credentials: credentialsProvider
      });
      
      // Create DynamoDB Document client
      const docClient = DynamoDBDocumentClient.from(dynamoClient, {
        marshallOptions: { removeUndefinedValues: true }
      });
      
      // Create scan command
      const tableName = 'erikaandrew_wedding';
      const scanCommand = new ScanCommand({
        TableName: tableName,
        ConsistentRead: true
      });
      
      // Execute scan command
      const result = await docClient.send(scanCommand);
      const allData = result.Items || [];
      
      // Filter for attending guests and "not yet" guests
      const attending = allData.filter(item => item.rsvp === "attending" || item.rsvp === "not yet");
      
      setRsvpData(allData);
      setAttendingGuests(attending);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching RSVP data:', err);
      setError(`Failed to load RSVP data: ${err.message}`);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRsvpData();
  }, [fetchRsvpData]);

  // Drag and drop handlers
  const handleDragStart = (e, guest) => {
    setDraggedGuest(guest);
    e.dataTransfer.effectAllowed = 'move';
    e.target.classList.add('dragging');
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove('dragging');
    setDraggedGuest(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, tableId) => {
    e.preventDefault();
    const tableElement = e.currentTarget;
    tableElement.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    const tableElement = e.currentTarget;
    tableElement.classList.remove('drag-over');
  };

  const handleDrop = (e, targetTableId) => {
    e.preventDefault();
    
    if (!draggedGuest) return;

    // Remove drag-over styling
    const tableElement = e.currentTarget;
    tableElement.classList.remove('drag-over');

    setTables(prevTables => {
      const updatedTables = prevTables.map(table => {
        // Remove guest from any table they might be in
        const filteredGuests = table.guests.filter(g => g.id !== draggedGuest.id);
        
        // Add guest to target table if it's the right table
        if (table.id === targetTableId) {
          // Check if table has capacity
          const tableCapacity = getTableCapacity(table.id);
          if (filteredGuests.length < tableCapacity) {
            return {
              ...table,
              guests: [...filteredGuests, draggedGuest]
            };
          }
        }
        
        return {
          ...table,
          guests: filteredGuests
        };
      });
      
      return updatedTables;
    });
    
    // Mark tables as edited
    setEditedTables(prev => {
      const newEdited = new Set(prev);
      // Mark the target table as edited
      newEdited.add(targetTableId);
      // Mark the source table as edited (if guest was moved from another table)
      const sourceTable = tables.find(table => table.guests.some(g => g.id === draggedGuest.id));
      if (sourceTable && sourceTable.id !== targetTableId) {
        newEdited.add(sourceTable.id);
      }
      return newEdited;
    });
    
    setDraggedGuest(null);
  };

  const removeGuestFromTable = (tableId, guestId) => {
    setTables(prevTables => 
      prevTables.map(table => 
        table.id === tableId 
          ? { ...table, guests: table.guests.filter(g => g.id !== guestId) }
          : table
      )
    );
    
    // Mark table as edited
    setEditedTables(prev => {
      const newEdited = new Set(prev);
      newEdited.add(tableId);
      return newEdited;
    });
  };

  const getGuestDisplayName = (guest) => {
    const firstName = guest.firstName || '';
    const lastName = guest.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || guest.id || 'Unknown Guest';
  };

  const isGuestNotYet = (guest) => {
    return guest.rsvp === "not yet";
  };

  const getFilteredUnseatedGuests = () => {
    // Get all unseated guests
    const unseatedGuests = attendingGuests.filter(guest => 
      !tables.some(table => table.guests.some(g => g.id === guest.id))
    );
    
    // Filter by search term if provided
    if (!guestSearchTerm.trim()) {
      return unseatedGuests;
    }
    
    const searchLower = guestSearchTerm.toLowerCase();
    return unseatedGuests.filter(guest => {
      const fullName = getGuestDisplayName(guest).toLowerCase();
      const email = guest.id.toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  };

  const handleTableNameEdit = (tableId) => {
    setEditingTableName(tableId);
  };

  const handleTableNameSave = async (tableId, newName) => {
    if (newName.trim()) {
      setTableNames(prev => ({
        ...prev,
        [tableId]: newName.trim()
      }));
      
      // Immediately save this table's name to DynamoDB
      try {
        console.log(`Saving table name for table ${tableId}: ${newName.trim()}`);
        
        const response = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${currentVersion}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            seatingData: [{
              tableNumber: tableId,
              tableName: newName.trim(),
              capacity: getTableCapacity(tableId),
              guests: tables.find(t => t.id === tableId)?.guests.map(guest => guest.id) || []
            }]
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const result = await response.json();
        console.log(`Table name saved successfully for table ${tableId}:`, result);
        
      } catch (err) {
        console.error(`Error saving table name for table ${tableId}:`, err);
        // Optionally show an error message to the user
      }
    }
    setEditingTableName(null);
  };

  const handleTableNameCancel = () => {
    setEditingTableName(null);
  };

  const getTableDisplayName = (tableId) => {
    return tableNames[tableId] || `Table ${tableId}`;
  };

  const createNewVersion = async () => {
    console.log('=== Starting new version creation process ===');
    
    const versionName = prompt('Enter a name for the new version:');
    console.log('User entered version name:', versionName);
    
    if (versionName && versionName.trim()) {
      const newVersion = versionName.trim();
      console.log('Trimmed version name:', newVersion);
      console.log('Current versions list:', versions);
      
      if (!versions.includes(newVersion)) {
        console.log('Version name is unique, proceeding with creation...');
        
        try {
          console.log('Step 1: Adding version to DynamoDB versions list...');
          // Add to DynamoDB versions list
          await saveVersions([...versions, newVersion], 'add', newVersion);
          console.log('Step 1 completed: Version added to DynamoDB versions list');
          
          console.log('Step 2: Checking if version already has seating data...');
          // Check if the version already has seating data in DynamoDB
          const checkResponse = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${encodeURIComponent(newVersion)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          console.log('Check response status:', checkResponse.status);
          
          let hasExistingData = false;
          if (checkResponse.ok) {
            const checkResult = await checkResponse.json();
            console.log('Check response data:', checkResult);
            hasExistingData = checkResult.seatingData && checkResult.seatingData.length > 0;
            console.log('Version has existing data:', hasExistingData);
          }
          
          if (!hasExistingData) {
            console.log('Step 3: Creating basic empty table structure...');
            // Create basic empty table structure for the new version
            const emptyTables = Array.from({ length: NUMBER_OF_TABLES }, (_, index) => ({
              tableNumber: index + 1,
              tableName: `Table ${index + 1}`,
              capacity: TABLE_CAPACITY,
              guests: []
            }));
            
            console.log(`Created ${emptyTables.length} empty tables for version: ${newVersion}`);
            console.log('Sample table structure:', emptyTables[0]);
            
            console.log('Step 4: Saving empty table structure to DynamoDB...');
            // Save the empty table structure to DynamoDB
            const response = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${encodeURIComponent(newVersion)}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                seatingData: emptyTables
              })
            });
            
            console.log('API response status:', response.status);
            console.log('API response headers:', response.headers);
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('API response error:', errorText);
              throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }
            
            const result = await response.json();
            console.log('Step 4 completed: Empty table structure saved to DynamoDB');
            console.log('API response result:', result);
          } else {
            console.log('Step 3-4 skipped: Version already has seating data, no need to create empty tables');
          }
          
          console.log('Step 5: Switching to new version...');
          // Switch to the new version
          setCurrentVersion(newVersion);
          console.log('Current version set to:', newVersion);
          
          console.log('Step 6: Clearing current seating data...');
          // Clear current seating data and load the new version
          setTables(prevTables => 
            prevTables.map(table => ({
              ...table,
              guests: []
            }))
          );
          setTableNames({});
          setTableCapacities({});
          setEditedTables(new Set());
          setHasLoadedSeating(false);
          console.log('Step 6 completed: Current data cleared');
          
          console.log('Step 7: Loading newly created version data...');
          // Load the newly created version data
          setTimeout(() => {
            console.log('Starting loadSeatingChart for new version...');
            loadSeatingChart();
          }, 100);
          
          console.log('=== New version creation completed successfully ===');
          
        } catch (err) {
          console.error('=== Error during new version creation ===');
          console.error('Error details:', err);
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
          alert(`Failed to create new version: ${err.message}`);
        }
      } else {
        console.log('Version name already exists, aborting creation');
        alert('Version name already exists!');
      }
    } else {
      console.log('No version name provided or empty string, aborting creation');
    }
  };

  const switchVersion = (version) => {
    console.log('Switching to version:', version);
    setCurrentVersion(version);
    // Only clear table names cache when switching versions
    setTableNames({});
    setHasLoadedSeating(false);
    // Load data for the new version
    setTimeout(() => loadSeatingChart(), 100);
  };

  const deleteVersion = async (version) => {
    if (version === 'default') {
      alert('Cannot delete the default version!');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete version "${version}"?`)) {
      const newVersions = versions.filter(v => v !== version);
      await saveVersions(newVersions, 'delete', version);
      
      if (currentVersion === version) {
        setCurrentVersion('default');
        // Only clear table names cache when switching to default
        setTableNames({});
        setHasLoadedSeating(false);
        setTimeout(() => loadSeatingChart(), 100);
      }
    }
  };

  const cloneVersion = async () => {
    console.log('=== Starting version clone process ===');
    console.log('Cloning from version:', currentVersion);
    
    const newVersionName = prompt(`Enter a name for the cloned version (copying from "${currentVersion}"):`);
    console.log('User entered clone name:', newVersionName);
    
    if (newVersionName && newVersionName.trim()) {
      const clonedVersionName = newVersionName.trim();
      console.log('Trimmed clone name:', clonedVersionName);
      console.log('Current versions list:', versions);
      
      if (!versions.includes(clonedVersionName)) {
        console.log('Clone name is unique, proceeding with cloning...');
        
        try {
          console.log('Step 1: Adding cloned version to DynamoDB versions list...');
          // Add to DynamoDB versions list
          await saveVersions([...versions, clonedVersionName], 'add', clonedVersionName);
          console.log('Step 1 completed: Cloned version added to DynamoDB versions list');
          
          console.log('Step 2: Loading current version data to clone...');
          // Load the current version's data to clone it
          const currentDataResponse = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${encodeURIComponent(currentVersion)}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            }
          });
          
          console.log('Current data response status:', currentDataResponse.status);
          
          if (!currentDataResponse.ok) {
            const errorText = await currentDataResponse.text();
            console.error('Error loading current version data:', errorText);
            throw new Error(`Failed to load current version data: ${currentDataResponse.status}`);
          }
          
          const currentData = await currentDataResponse.json();
          console.log('Current version data loaded:', currentData);
          
          if (currentData.seatingData && currentData.seatingData.length > 0) {
            console.log('Step 3: Cloning seating data to new version...');
            // Clone the seating data to the new version
            const clonedData = currentData.seatingData.map(table => ({
              ...table,
              // Keep all the same data (guests, table names, capacities)
            }));
            
            console.log(`Cloning ${clonedData.length} tables to version: ${clonedVersionName}`);
            console.log('Sample cloned table:', clonedData[0]);
            
            console.log('Step 4: Saving cloned data to DynamoDB...');
            // Save the cloned data to DynamoDB
            const saveResponse = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${encodeURIComponent(clonedVersionName)}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                seatingData: clonedData
              })
            });
            
            console.log('Save response status:', saveResponse.status);
            
            if (!saveResponse.ok) {
              const errorText = await saveResponse.text();
              console.error('Error saving cloned data:', errorText);
              throw new Error(`Failed to save cloned data: ${saveResponse.status}`);
            }
            
            const saveResult = await saveResponse.json();
            console.log('Step 4 completed: Cloned data saved to DynamoDB');
            console.log('Save result:', saveResult);
          } else {
            console.log('Step 3-4 skipped: Current version has no seating data to clone');
          }
          
          console.log('Step 5: Switching to cloned version...');
          // Switch to the cloned version
          setCurrentVersion(clonedVersionName);
          console.log('Current version set to:', clonedVersionName);
          
          console.log('Step 6: Loading cloned version data...');
          // Only clear table names cache and load the cloned version data
          setTableNames({});
          setHasLoadedSeating(false);
          setTimeout(() => {
            console.log('Starting loadSeatingChart for cloned version...');
            loadSeatingChart();
          }, 100);
          
          console.log('=== Version clone completed successfully ===');
          
        } catch (err) {
          console.error('=== Error during version cloning ===');
          console.error('Error details:', err);
          console.error('Error message:', err.message);
          console.error('Error stack:', err.stack);
          alert(`Failed to clone version: ${err.message}`);
        }
      } else {
        console.log('Clone name already exists, aborting clone');
        alert('Version name already exists!');
      }
    } else {
      console.log('No clone name provided or empty string, aborting clone');
    }
  };

  const handleCapacityEdit = (tableId) => {
    setEditingCapacity(tableId);
  };

  const handleCapacitySave = (tableId, newCapacity) => {
    const capacity = parseInt(newCapacity);
    if (capacity > 0 && capacity <= 20) {
      setTableCapacities(prev => ({
        ...prev,
        [tableId]: capacity
      }));
      
      // Mark table as edited
      setEditedTables(prev => {
        const newEdited = new Set(prev);
        newEdited.add(tableId);
        return newEdited;
      });
    } else {
      alert('Capacity must be between 1 and 20');
    }
    setEditingCapacity(null);
  };

  const handleCapacityCancel = () => {
    setEditingCapacity(null);
  };

  const getTableCapacity = (tableId) => {
    return tableCapacities[tableId] || TABLE_CAPACITY;
  };

  const addNewTable = () => {
    const newTable = {
      id: nextTableId,
      name: `Table ${nextTableId}`,
      guests: [],
      maxCapacity: TABLE_CAPACITY
    };
    
    setTables(prevTables => [...prevTables, newTable]);
    setNextTableId(prevId => prevId + 1);
    
    // Mark the new table as edited so it gets saved
    setEditedTables(prev => {
      const newEdited = new Set(prev);
      newEdited.add(newTable.id);
      return newEdited;
    });
  };

  const removeTable = (tableId) => {
    if (window.confirm(`Are you sure you want to remove Table ${tableId}? This will move all guests to the unseated list.`)) {
      // Move all guests from this table back to unseated
      const tableToRemove = tables.find(table => table.id === tableId);
      if (tableToRemove && tableToRemove.guests.length > 0) {
        // Guests will automatically appear in the unseated list since they're no longer assigned to any table
        console.log(`Removing table ${tableId} with ${tableToRemove.guests.length} guests`);
      }
      
      // Remove the table
      setTables(prevTables => prevTables.filter(table => table.id !== tableId));
      
      // Remove table name and capacity from state
      setTableNames(prev => {
        const newNames = { ...prev };
        delete newNames[tableId];
        return newNames;
      });
      
      setTableCapacities(prev => {
        const newCapacities = { ...prev };
        delete newCapacities[tableId];
        return newCapacities;
      });
      
      // Mark as edited to save the removal
      setEditedTables(prev => {
        const newEdited = new Set(prev);
        newEdited.add(tableId); // Mark as edited so the removal gets saved
        return newEdited;
      });
    }
  };

  const handleSaveSeatingChart = async () => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      // Save all tables to ensure proper synchronization when tables are added/removed
      const seatingData = tables.map(table => ({
        tableNumber: table.id,
        tableName: getTableDisplayName(table.id),
        capacity: tableCapacities[table.id] || TABLE_CAPACITY,
        guests: table.guests.map(guest => guest.id) // Use email as guest identifier
      }));
      
      console.log('Saving seating data for all tables:', seatingData);
      console.log('Edited tables:', Array.from(editedTables));
      
      // Make API request to save seating chart
      const response = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${currentVersion}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          seatingData: seatingData
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Seating chart saved successfully:', result);
      setSaveSuccess(true);
      
      // Clear the edited tables set after successful save
      setEditedTables(new Set());
      
      // Reload seating chart data after successful save
      await loadSeatingChart();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error saving seating chart:', err);
      setSaveError(`Failed to save seating chart: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="seating-chart-loading">Loading dashboard...</div>;
  }

  if (error) {
    return <div className="seating-chart-error">{error}</div>;
  }

  return (
    <div className="seating-chart-container">
      <div className="seating-chart-header">
        <h1>Wedding Seating Chart</h1>
        <div className="seating-chart-stats">
          <p>Total Attending: {attendingGuests.length}</p>
          <p>Seated: {tables.reduce((total, table) => total + table.guests.length, 0)}</p>
          <p>Unseated: {attendingGuests.length - tables.reduce((total, table) => total + table.guests.length, 0)}</p>
          <p>Version: {currentVersion}</p>
          {isLoadingSeating && <p>Loading seating data...</p>}
        </div>
        <div className="seating-chart-actions">
          <div className="version-controls">
            {isLoadingVersions ? (
              <span className="versions-loading">Loading versions...</span>
            ) : (
              <>
                <select 
                  value={currentVersion} 
                  onChange={(e) => switchVersion(e.target.value)}
                  className="version-select"
                >
                  {versions.map(version => (
                    <option key={version} value={version}>{version}</option>
                  ))}
                </select>
                <button onClick={createNewVersion} className="new-version-button">
                  New Version
                </button>
                <button onClick={cloneVersion} className="clone-version-button">
                  Clone Version
                </button>
                {currentVersion !== 'default' && (
                  <button onClick={() => deleteVersion(currentVersion)} className="delete-version-button">
                    Delete Version
                  </button>
                )}
              </>
            )}
          </div>
          <button onClick={fetchRsvpData} className="refresh-button">
            Refresh Data
          </button>
          <button 
            onClick={() => {
              console.log('Load button clicked - loading version:', currentVersion);
              setHasLoadedSeating(false);
              setTimeout(() => loadSeatingChart(), 100);
            }} 
            className="load-seating-button"
            disabled={isLoadingSeating}
          >
            {isLoadingSeating ? 'Loading Seating Data...' : `Load ${currentVersion}`}
          </button>
          <button 
            onClick={handleSaveSeatingChart} 
            className="save-seating-button"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 
             editedTables.size > 0 ? `Save Seating Chart (${editedTables.size} tables)` : 'Save Seating Chart'}
          </button>
          <button 
            onClick={async () => {
              console.log('Testing API call...');
              try {
                const response = await fetch(`https://hg9ykqmu3m.execute-api.us-west-2.amazonaws.com/prod/seating?version=${currentVersion}`, {
                  method: 'GET',
                  headers: { 'Content-Type': 'application/json' }
                });
                const data = await response.json();
                console.log('Test API response:', data);
                alert(`API Test: ${response.status} - ${JSON.stringify(data, null, 2)}`);
              } catch (err) {
                console.error('Test API error:', err);
                alert(`API Test Error: ${err.message}`);
              }
            }}
            className="test-button"
          >
            Test API
          </button>
        </div>
      </div>

      {saveError && (
        <div className="seating-chart-error-message">
          {saveError}
        </div>
      )}

      {saveSuccess && (
        <div className="seating-chart-success-message">
          Seating chart saved successfully!
        </div>
      )}

      <div className="seating-chart-layout">
        {/* Left side - Unseated guests */}
        <div className="guests-panel">
          <h2>Unseated Guests</h2>
          <div className="guests-search-container">
            <input
              type="text"
              placeholder="Search guests by name or email..."
              value={guestSearchTerm}
              onChange={(e) => setGuestSearchTerm(e.target.value)}
              className="guests-search-input"
            />
            {guestSearchTerm && (
              <button
                onClick={() => setGuestSearchTerm('')}
                className="clear-search-button"
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {guestSearchTerm && (
            <div className="search-results-info">
              <span>
                {getFilteredUnseatedGuests().length} of {attendingGuests.filter(guest => 
                  !tables.some(table => table.guests.some(g => g.id === guest.id))
                ).length} unseated guests
              </span>
            </div>
          )}
          <div 
            className="guests-list"
            onDragOver={handleDragOver}
            onDragEnter={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add('drag-over');
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove('drag-over');
              
              if (draggedGuest) {
                // Find which table the guest was removed from
                const sourceTable = tables.find(table => table.guests.some(g => g.id === draggedGuest.id));
                
                // Remove guest from any table they might be in
                setTables(prevTables => 
                  prevTables.map(table => ({
                    ...table,
                    guests: table.guests.filter(g => g.id !== draggedGuest.id)
                  }))
                );
                
                // Mark the source table as edited
                if (sourceTable) {
                  setEditedTables(prev => {
                    const newEdited = new Set(prev);
                    newEdited.add(sourceTable.id);
                    return newEdited;
                  });
                }
                
                setDraggedGuest(null);
              }
            }}
          >
            {getFilteredUnseatedGuests().map(guest => (
              <div
                key={guest.id}
                className={`guest-item ${isGuestNotYet(guest) ? 'guest-not-yet' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, guest)}
                onDragEnd={handleDragEnd}
              >
                <span className="guest-name">{getGuestDisplayName(guest)}</span>
                {guest.mealChoice && (
                  <span className="meal-indicator">
                    {guest.mealChoice === 'beef_salmon' ? '🍖' : 
                     guest.mealChoice === 'vegetarian' ? '🥬' : 
                     guest.mealChoice === 'kids_meal' ? '🍕' : '🍽️'}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right side - Tables */}
        <div className="tables-panel">
          <div className="tables-header">
            <h2>Tables</h2>
            <button onClick={addNewTable} className="add-table-button">
              + Add Table
            </button>
          </div>
          <div className="tables-grid">
            {tables.map(table => (
              <div
                key={table.id}
                className={`table-container ${table.guests.length >= getTableCapacity(table.id) ? 'table-full' : ''} ${editedTables.has(table.id) ? 'table-edited' : ''}`}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, table.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, table.id)}
              >
                <div className="table-header">
                  <div className="table-name-container">
                    {editingTableName === table.id ? (
                      <div className="table-name-edit">
                        <input
                          type="text"
                          defaultValue={getTableDisplayName(table.id)}
                          className="table-name-input"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleTableNameSave(table.id, e.target.value);
                            } else if (e.key === 'Escape') {
                              handleTableNameCancel();
                            }
                          }}
                          onBlur={(e) => handleTableNameSave(table.id, e.target.value)}
                        />
                        <button
                          onClick={() => handleTableNameSave(table.id, document.querySelector('.table-name-input').value)}
                          className="table-name-save-btn"
                          title="Save name"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleTableNameCancel}
                          className="table-name-cancel-btn"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="table-name-display">
                        <h3>{getTableDisplayName(table.id)}</h3>
                        <button
                          onClick={() => handleTableNameEdit(table.id)}
                          className="table-name-edit-btn"
                          title="Edit table name"
                        >
                          ✎
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="table-controls">
                    <span className="table-count">
                      {table.guests.length}/{getTableCapacity(table.id)}
                      {editingCapacity === table.id ? (
                        <span className="capacity-edit">
                          <input
                            type="number"
                            min="1"
                            max="20"
                            defaultValue={getTableCapacity(table.id)}
                            className="capacity-input"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleCapacitySave(table.id, e.target.value);
                              } else if (e.key === 'Escape') {
                                handleCapacityCancel();
                              }
                            }}
                            onBlur={(e) => handleCapacitySave(table.id, e.target.value)}
                          />
                          <button
                            onClick={() => handleCapacitySave(table.id, document.querySelector('.capacity-input').value)}
                            className="capacity-save-btn"
                            title="Save capacity"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCapacityCancel}
                            className="capacity-cancel-btn"
                            title="Cancel"
                          >
                            ✕
                          </button>
                        </span>
                      ) : (
                        <button
                          onClick={() => handleCapacityEdit(table.id)}
                          className="capacity-edit-btn"
                          title="Edit capacity"
                        >
                          ⚙️
                        </button>
                      )}
                      {editedTables.has(table.id) && <span className="edited-indicator" title="Table has been modified"> ●</span>}
                    </span>
                    <button
                      onClick={() => removeTable(table.id)}
                      className="remove-table-btn"
                      title="Remove table"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                <div className="table-guests">
                  {table.guests.map(guest => (
                    <div 
                      key={guest.id} 
                      className={`seated-guest ${isGuestNotYet(guest) ? 'guest-not-yet' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, guest)}
                      onDragEnd={handleDragEnd}
                    >
                      <span className="guest-name">{getGuestDisplayName(guest)}</span>
                      {guest.mealChoice && (
                        <span className="meal-indicator">
                          {guest.mealChoice === 'beef_salmon' ? '🍖' : 
                           guest.mealChoice === 'vegetarian' ? '🥬' : 
                           guest.mealChoice === 'kids_meal' ? '🍕' : '🍽️'}
                        </span>
                      )}
                      <button
                        className="remove-guest-btn"
                        onClick={() => removeGuestFromTable(table.id, guest.id)}
                        title="Remove from table"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {table.guests.length === 0 && (
                    <div className="empty-table-message">
                      Drag guests here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SeatingChart; 